import { createSelector } from 'reselect';

import { StateType } from '../index';
import * as Immutable from 'immutable';
import moment from 'moment';
import { CompetitionImmutable } from '../../components/Competitions/CompetitionsTypes';
import { makeMomentDate } from '../../shared/helpers';

export const DISPLAY_COMPETITION_WITHIN_MONTHS = 3;

export const competitionSelector = (state: StateType) => state.getIn(['competitions', 'competition']);
export const competitionUserSelector = (state: StateType) =>
  state.getIn(['competitions', 'competition', 'leaderboard', 'user']) as Immutable.Map<string, any>;
export const logoutCompetitionIdSelector = (state: StateType) => state.getIn(['competitions', 'logoutCompetition']);
export const competitionNameSelector = (state: StateType) => state.getIn(['competitions', 'competition', 'name']);
export const competitionAggregationSelector = (state: StateType) => state.getIn(['competitions', 'aggregation']);
export const competitionAggregationChoicesSelector = (state: StateType) => state.getIn(['competitions', 'aggregationChoices']);
export const competitionIsFetchingSelector = (state: StateType) => state.getIn(['competitions', 'isFetching']);
export const competitionFetchTableCompetitionsSelector = (state: StateType) => state.getIn(['competitions', 'fetchTableCompetitions']);
export const competitionFetchTableReferralsSelector = (state: StateType) => state.getIn(['competitions', 'fetchReferralsTable']) as boolean;
export const competitionFetchTablePowerPoolSelector = (state: StateType) => state.getIn(['competitions', 'fetchPowerPoolTable']) as boolean;
export const competitionFetchCompetitionSelector = (state: StateType) => state.getIn(['competitions', 'fetchCompetition']);
export const competitionFetchAgreeGuidelinesSelector = (state: StateType) => state.getIn(['competitions', 'fetchAgreeWithGuidelines']);
export const competitionFetchOptionsSelector = (state: StateType) => state.getIn(['competitions', 'fetchOptions']);
export const competitionSelectedColumnsModalSelector = (state: StateType) => state.getIn(['competitions', 'selectedColumnsModal']);
export const competitionShowGuidelinesModalSelector = (state: StateType) => state.getIn(['competitions', 'showGuidelinesModal']);

export const competitionModalTableDataSelector = (state: StateType) => state.getIn(['competitions', 'modalTableData']);
export const competitionHideAgreementModalSelector = (state: StateType) => state.getIn(['competitions', 'hideAgreementModal']);
export const competitionFetchUserCompetitionsSelector = (state: StateType) => state.getIn(['competitions', 'fetchUserCompetitions']);
export const competitionUserCompetitionResultsSelector = (state: StateType) =>
  state.getIn(['competitions', 'userCompetitions', 'results']) as Immutable.List<CompetitionImmutable>;
export const filterNewCompetitions = (competitions: Immutable.List<CompetitionImmutable>) => {
  const now = moment();
  return competitions?.filter(competition => {
    const endDate = moment(competition.get('endDate'));
    return !endDate.isValid() || now.diff(endDate, 'months') <= DISPLAY_COMPETITION_WITHIN_MONTHS;
  });
};

function getChallengeScoreFromCompetitionList(competitions: Immutable.List<CompetitionImmutable>) {
  const challengeCompetition = competitions?.filter(competition => competition.get('name') === 'Challenge');
  if (!challengeCompetition || challengeCompetition?.size === 0) {
    return 0;
  }
  return challengeCompetition?.get(0)?.getIn(['leaderboard', 'score']) as number | undefined;
}

export const challengeScoreCompetitionSelector = createSelector(
  competitionUserCompetitionResultsSelector,
  getChallengeScoreFromCompetitionList
);
export const competitionUserCompetitionsDropdownOptionsSelector = createSelector(
  competitionUserCompetitionResultsSelector,
  filterNewCompetitions
);
export const competitionEnrolledCompetitionsSelector = createSelector(competitionUserCompetitionResultsSelector, competitions =>
  Immutable.List<CompetitionImmutable>(competitions).filter(competition => {
    const endDate = makeMomentDate(competition.get('endDate'));
    return competition.get('status') === 'ACCEPTED' && (!endDate.isValid() || endDate.isAfter(moment.now()));
  })
);
export const competitionAllCompetitionsSelector = (state: StateType) => state.getIn(['competitions', 'allCompetitions']);
export const competitionAllSchemaSelector = (state: StateType) => state.getIn(['competitions', 'allSchema']);
export const competitionFetchAllCompetitionsSelector = (state: StateType) => state.getIn(['competitions', 'fetchAllCompetitions']);
export const competitionSelectedColumnsSelector = (state: StateType) => state.getIn(['competitions', 'selectedColumns']);
export const competitionTableCompetitionsSelector = (state: StateType) =>
  state.getIn(['competitions', 'tableCompetitions']) as Immutable.Map<string, any>;
export const prizeTableCompetitionsSelector = (state: StateType) =>
  state.getIn(['competitions', 'tablePrizes']) as Immutable.Map<string, any>;
export const referralsTableCompetitionsSelector = (state: StateType) =>
  state.getIn(['competitions', 'tableReferrals']) as Immutable.Map<string, any>;
export const powerPoolTableCompetitionsSelector = (state: StateType) =>
  state.getIn(['competitions', 'tablePowerPool']) as Immutable.Map<string, any>;
export const spcLeaderboardTableCompetitionsSelector = (state: StateType) =>
  state.getIn(['competitions', 'tableSpcLeaderboard']) as Immutable.Map<string, any>;
export const competitionFetchSpcLeaderboardTableSelector = (state: StateType) =>
  state.getIn(['competitions', 'fetchSpcLeaderboardTable']) as boolean;
export const competitionOptionsSelector = (state: StateType) => state.getIn(['competitions', 'options']);
export const competitionModalTableOptionsSelector = (state: StateType) =>
  state.getIn(['competitions', 'modalTableOptions']) as Immutable.Map<string, any>;
export const competitionCountryOptionsSelector = (state: StateType) => state.getIn(['competitions', 'countryOptions']);
export const competitionUniversityCountryOptionsSelector = (state: StateType) =>
  state.getIn(['competitions', 'universityCountryOptions']) as Immutable.Map<string, string>;
export const competitionLevelOptionsSelector = (state: StateType) => state.getIn(['competitions', 'levelOptions']);

export const competitionAggregateFilterDefaultSelector = (state: StateType) =>
  state.getIn(['competitions', 'aggregationChoices', 0, 'value']);
export const competitionAggregateFilterDefaultLabelSelector = (state: StateType) =>
  state.getIn(['competitions', 'aggregationChoices', 0, 'label']);

export const isFetchingCompetitions = createSelector(
  competitionIsFetchingSelector,
  competitionFetchTableCompetitionsSelector,
  competitionFetchCompetitionSelector,
  competitionFetchTableReferralsSelector,
  competitionFetchTablePowerPoolSelector,
  (isFetching, fetchTableCompetitions, fetchCompetition, fetchReferrals, fetchPowerPool) =>
    isFetching || fetchTableCompetitions || fetchCompetition || fetchReferrals || fetchPowerPool
);
